import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import dayjs from 'dayjs';
import { z } from 'zod';

import { type EventLogInputType } from '@/api/eventLog';
import { Button } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { DateISOFormat } from '@/constants/formats';

type NoteFormValues = {
  date: Date | undefined;
  details: string;
};

type Props = {
  defaultNote?: EventLogInputType;
  isSaving?: boolean;
  onSave: (note: EventLogInputType) => Promise<void> | void;
  onCancel: () => void;
};

const NotesPanelDrawerEdit = ({ defaultNote, isSaving, onSave, onCancel }: Props) => {
  const formSchema = z.object({
    date: z.date({ message: t`Please choose a date` }),
    details: z
      .string()
      .trim()
      .min(1, t`Please enter a note`),
  });

  const form = useAppForm({
    defaultValues: {
      date: defaultNote ? dayjs(defaultNote.date).utc().toDate() : dayjs().startOf('d').toDate(),
      details: defaultNote?.rawDetails?.toString() ?? '',
    } satisfies NoteFormValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.parse(value);
      await onSave({
        id: defaultNote?.id,
        date: `${dayjs(parsed.date).format(DateISOFormat)}T00:00:00Z`,
        details: parsed.details,
        rawDetails: parsed.details,
      });
    },
  });
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const isBusy = Boolean(isSaving) || isSubmitting;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <div className="flex w-full flex-1 flex-row items-start gap-x-3 pt-5 pb-1">
        <form.AppField name="date">
          {(field) => (
            <field.Field className="w-25">
              <field.DatePicker size="small" />
              <field.Error />
            </field.Field>
          )}
        </form.AppField>
        <form.AppField name="details">
          {(field) => (
            <field.Field className="mb-0! flex-1">
              <field.TextArea
                placeholder={t`Type your note here`}
                size="small"
                rows={3}
                maxLength={200}
              />
              <field.Error />
            </field.Field>
          )}
        </form.AppField>
        <div className="flex w-20 flex-col gap-y-3">
          <form.AppForm>
            <form.SubmitButton variant="primary" size="small">
              <Trans>Save</Trans>
            </form.SubmitButton>
          </form.AppForm>
          <Button size="small" disabled={isBusy} onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </div>
    </form>
  );
};

export default NotesPanelDrawerEdit;
